import { z } from '../../../zod';

type PaymentOptionFields = {
  type: 'manual' | 'partial' | 'full';
  manualType?: 'invoice' | 'cash_on_delivery' | null;
};

const ALLOWED_PAYMENT_OPTION_COMBINATIONS = new Set([
  'cash_on_delivery',
  'full',
  'full+cash_on_delivery',
  'full+invoice',
  'full+partial',
  'invoice',
  'partial',
]);

const PAYMENT_OPTION_COMBINATION_ORDER = {
  full: 0,
  invoice: 1,
  partial: 2,
  cash_on_delivery: 3,
} as const;

const getPaymentOptionCombinationKey = (option: {
  type: 'manual' | 'partial' | 'full';
  manualType?: 'invoice' | 'cash_on_delivery' | null;
}) => {
  if (option.type === 'full') {
    return 'full';
  }

  if (option.type === 'partial') {
    return 'partial';
  }

  if (option.manualType === 'invoice') {
    return 'invoice';
  }

  if (option.manualType === 'cash_on_delivery') {
    return 'cash_on_delivery';
  }

  return null;
};

export const SUPPORTED_PAYMENT_OPTION_COMBINATIONS_DESCRIPTION =
  '`full`, `invoice`, `partial`, `cash_on_delivery`, `full + invoice`, `full + partial`, or `full + cash_on_delivery`';

export const UNSUPPORTED_PAYMENT_OPTION_COMBINATION_MESSAGE =
  'Only supported payment option combinations are allowed. Enable one of: ' +
  SUPPORTED_PAYMENT_OPTION_COMBINATIONS_DESCRIPTION +
  '.';

export const hasSupportedEnabledPaymentOptionCombination = (
  paymentOptions?: Array<{
    type: 'manual' | 'partial' | 'full';
    enabled?: boolean | null;
    manualType?: 'invoice' | 'cash_on_delivery' | null;
  }> | null,
) => {
  if (!paymentOptions) {
    return true;
  }

  const enabledOptionKeys = Array.from(
    new Set(
      paymentOptions
        .filter(option => option.enabled === true)
        .map(getPaymentOptionCombinationKey)
        .filter(
          (key): key is NonNullable<ReturnType<typeof getPaymentOptionCombinationKey>> => !!key,
        ),
    ),
  ).sort((a, b) => PAYMENT_OPTION_COMBINATION_ORDER[a] - PAYMENT_OPTION_COMBINATION_ORDER[b]);

  if (enabledOptionKeys.length === 0) {
    return false;
  }

  return ALLOWED_PAYMENT_OPTION_COMBINATIONS.has(enabledOptionKeys.join('+'));
};

const withPaymentOptionValidation = <T extends z.ZodType<PaymentOptionFields>>(schema: T): T => {
  return schema.refine(
    data => {
      if (data.type === 'manual') {
        return !!data.manualType;
      }

      return true;
    },
    {
      message: '`manualType` is required when `type` is `manual`.',
      path: ['manualType'],
    },
  ) as T;
};

const buildPagePaymentOptionBaseSchema = () => {
  return z.object({
    type: z.enum(['manual', 'partial', 'full']).meta({
      description:
        'Payment collection type. `manual` collects offline (invoice or cash on delivery). `partial` collects a deposit (`partialAmount`) now and the remainder offline. `full` collects the entire amount at checkout.',
    }),
    enabled: z.boolean().nullish(),
    name: z.string().meta({ description: 'Name of this payment option.', example: 'Pay in full' }),
    description: z.string().nullish().meta({
      description: 'Description of this payment option.',
    }),
    instructions: z.string().nullish().meta({
      description: 'Instructions shown to the customer for completing payment.',
      example: 'Bank details will be sent via email after checkout.',
    }),
    partialAmount: z.number().nullish().meta({
      description: 'Partial amount to be collected at checkout. Applies to `partial` type only.',
    }),
    showPaymentButton: z.boolean().nullish().meta({
      description:
        'Whether to show this payment option to the customer at checkout. Enabled payment options should set this to `true`.',
      example: true,
    }),
    manualType: z.enum(['invoice', 'cash_on_delivery']).nullish().meta({
      description:
        'Variant of the manual payment flow. `invoice` sends the customer a confirmation with an invoice and marks the order as unpaid — use this when you will collect payment separately (e.g. bank transfer). `cash_on_delivery` behaves identically but is labelled for COD scenarios. Applies only when `type` is `manual`.',
      example: 'invoice',
    }),
  });
};

export const PagePaymentOptionInputValidationSchema = withPaymentOptionValidation(
  buildPagePaymentOptionBaseSchema(),
).meta({
  id: 'PagePaymentOptionInput',
  description:
    'Configures a single payment option shown at checkout. Only these enabled combinations are supported: ' +
    SUPPORTED_PAYMENT_OPTION_COMBINATIONS_DESCRIPTION +
    '. Multiple options can be combined (e.g. pay in full + pay via invoice) or a single option can be offered alone.\n\n' +
    '**Option types:**\n' +
    '- `full` — customer pays the entire amount immediately.\n' +
    '- `manual` — no payment is collected at checkout. A confirmation with an invoice is sent and the order is marked unpaid. Set \`manualType\` to \`invoice\` or \`cash_on_delivery\`.\n' +
    '- `partial` — customer pays `partialAmount` now; the remaining balance is due later and the order is marked as partially paid.\n\n' +
    'Digital files and event tickets are always issued immediately after checkout regardless of which option the customer selects. ' +
    '[Learn more](https://checkoutpage.com/docs/articles/manual-payments-pay-in-full-by-invoice-pay-deposit-or-cash-on-delivery)',
  example: [
    {
      type: 'full',
      enabled: true,
      name: 'Pay in full',
      description: 'Pay the full amount now',
      instructions: '',
      partialAmount: null,
      showPaymentButton: true,
    },
    {
      type: 'manual',
      enabled: false,
      name: 'Pay via invoice',
      description: 'Payment due in 30 days',
      instructions: 'Bank details will be sent via email after checkout.',
      partialAmount: null,
      showPaymentButton: true,
      manualType: 'invoice',
    },
    {
      type: 'partial',
      enabled: false,
      name: 'Pay a deposit',
      description: 'Pay a deposit now and the rest later',
      instructions: 'Pay the rest of your balance at a later date.',
      partialAmount: 5000,
      showPaymentButton: true,
    },
    {
      type: 'manual',
      enabled: true,
      name: 'Cash on delivery',
      description: 'Pay when you receive your order',
      instructions: 'Pay the full amount when your order is delivered.',
      partialAmount: null,
      showPaymentButton: true,
      manualType: 'cash_on_delivery',
    },
  ],
});

export const PagePaymentOptionResponseValidationSchema = buildPagePaymentOptionBaseSchema()
  .partial()
  .meta({
    id: 'PagePaymentOptionResponse',
    description: 'A payment option as returned in the checkout page response.',
  });
